/**
 * File: collectionsConstants.ts
 * Path: packages/config/src/firebase/collectionsConstants.ts
 * Description: Single source of truth for Firestore collection names and
 * Firebase Storage path prefixes used across all apps in the monorepo.
 */

export const FIRESTORE_COLLECTIONS = {
  users: "users",
  portfolio: "portfolio",
  public: "public",
  academy: "academy",
  leads: "leads",
  /** Sales preferences: stale-lead limits and the daily outreach goal (crmSettings/main). */
  crmSettings: "crmSettings",
  /** Liam's own wording for the WhatsApp messages the CRM types out (messageTemplates/main). */
  messageTemplates: "messageTemplates",
  /** Minutes spent on a lead or client, with who and at what rate (effort/effort.ts). */
  timeEntries: "timeEntries",
  /** Rand costs of winning or serving a lead or client. */
  costEntries: "costEntries",
  /** Shared monthly costs and ad spend, one document per YYYY-MM. */
  crmCosts: "crmCosts",
  retainers: "retainers",
  /** Help a care-plan client asked for: what, when, and the minutes it took (retainers/supportRequests.ts). */
  supportRequests: "supportRequests",
  /** Businesses we have won work from; a won lead keeps its id here. */
  clients: "clients",
  /** Every quote sent, attached to its lead and/or client (quotes/quotes.ts). */
  quotes: "quotes",
  invites: "invites",
  affiliates: "affiliates",
  referralEvents: "referralEvents",
  /** Admin wording over the coded SERVICES registry, keyed by service id. */
  serviceContent: "serviceContent",
  /** Copies of Google reviews, optionally attached to a portfolio project. */
  reviews: "reviews",
  /** Per-project "leave us a review" links sent to clients. */
  reviewRequests: "reviewRequests",
} as const;

/** Care plans (were `retainers`); a hand-added plan has no contract. Read this first, fall back to FIRESTORE_COLLECTIONS.retainers. */
export const CARE_PLANS_COLLECTION = "carePlans";
/** Who got which role, by whom (was `logs`). New records go here. */
export const ROLE_CHANGES_COLLECTION = "roleChanges";

export type FirestoreCollectionKey = keyof typeof FIRESTORE_COLLECTIONS;
export type FirestoreCollectionName =
  (typeof FIRESTORE_COLLECTIONS)[FirestoreCollectionKey];

export const STORAGE_PATHS = {
  academyCovers: "academy/covers",
  academyMedia: "academy/media",
  portfolioCovers: "portfolio/covers",
  portfolioGallery: "portfolio/gallery",
  userAvatars: "users/avatars",
} as const;

export type StoragePathKey = keyof typeof STORAGE_PATHS;
export type StoragePath = (typeof STORAGE_PATHS)[StoragePathKey];

export const storagePath = (
  ...segments: Array<string | number | undefined | null>
): string =>
  segments
    .filter(
      (s): s is string | number => s !== undefined && s !== null && s !== "",
    )
    .map(String)
    .join("/");
